import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  castErrorResult,
  ConfirmResult,
  editFormDataAF,
  errors,
  IApiError,
  IRideDto,
  IRideMetadata,
  IRideRemoveEmptyStationApiPayload,
  IRideRemovePassengersApiPayload,
  IRideTableDto,
  isSoftHardValidationSingleErrorResponse,
  isSoftValidationSingleErrorResponse,
  IStationInfo,
  IUpdateRideStationDto,
  Log,
  mapHardValidationErrorToResult,
  normalizeErrors,
  PassengerDeleteResultEnum,
  RideCrudModeKeyTuple,
  RidesApi,
  RideStationPanelMode,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultErrorInfo,
  simpleResultInvalidApiResponse,
} from "@ryde/shared";

import { useSoftValidationModalConfirm } from "../use-soft-validation-modal-confirms";
import { useClearStaleRideEditDto } from "./use-clear-stale-ride-edit-dto";
import { useRideNearbyActions } from "./use-ride-nearby-actions";
import { useRidesListActions } from "./use-rides-list-actions";

export const useRidesProcessing = () => {
  const { t } = useTranslation();
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const { replaceRide } = useRidesListActions();
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setRideDto = useSetRecoilState<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { clearStaleRideEditDtoCacheForRide } = useClearStaleRideEditDto();
  const { refetchAllNearbyByRideAndCurrent } = useRideNearbyActions();

  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();

  const removePassengersFromRide = useEvent(
    async (
      rideId: number,
      passengerIds: number[],
      bookingAction = PassengerDeleteResultEnum.NoAction,
    ): Promise<SimpleResult<void>> => {
      if (!passengerIds?.length) {
        return new SimpleResult({
          error: t(errors.ride.noPassengersChosenToRemoveFromRide, { rideId }),
        });
      }

      if (!rideId) {
        return new SimpleResult({
          error: t(errors.ride.cantRemovePassengersFromRideInvalidRideId),
        });
      }

      try {
        setLoading(true);

        const apiPayload: IRideRemovePassengersApiPayload = {
          passengerIds,
          bookingAction,
        };

        let result = await RidesApi.removePassengersFromRide(
          rideId,
          apiPayload,
        );

        if (isSoftValidationSingleErrorResponse(result?.data)) {
          setLoading(false);
          const softValidationResult = await confirmSoftValidationAction(
            result.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return new SimpleResult({ success: true });
          }

          setLoading(true);
          apiPayload.token = result.data.token;
          result = await RidesApi.removePassengersFromRide(rideId, apiPayload);
        } else if (isSoftHardValidationSingleErrorResponse(result?.data)) {
          const hardValidationError = result.data.validationErrors[0];
          setLoading(false);
          return mapHardValidationErrorToResult(hardValidationError);
        }

        if (!result?.success || !result.data) {
          if (result?.error) {
            return castErrorResult(result);
          }

          return new SimpleResult({
            error: t(errors.ride.errorRemovingPassengersInvalidAPI),
          });
        }

        const finalRideResult = result.data as IRideTableDto;

        replaceRide(finalRideResult);
        clearStaleRideEditDtoCacheForRide(rideId);
        await refetchAllNearbyByRideAndCurrent(rideId);

        await Log.successAndToast(
          t("ride.PASSENGERS_REMOVED_FROM_RIDE_SUCCESSFULLY"),
        );
        return new SimpleResult({ success: true });
      } catch (err) {
        const normalizedErrors = normalizeErrors(err as IApiError, t);
        setErrors(normalizedErrors);
        return new SimpleResult({ error: normalizedErrors });
      } finally {
        setLoading(false);
      }
    },
  );

  const removeEmptyStationFromRide = useEvent(
    async ({
      rideId,
      emptyStationId,
      secondaryId,
      skipMlPathByWaypoints,
    }: IRideRemoveEmptyStationApiPayload & {
      rideId: number;
    }): Promise<SimpleResult<void>> => {
      if (!emptyStationId) {
        return new SimpleResult({
          error: t(errors.ride.noStationsChosenToRemoveFromRide, { rideId }),
        });
      }

      if (!rideId) {
        return new SimpleResult({
          error: t(errors.ride.cantRemovePassengersFromRideInvalidRideId),
        });
      }

      try {
        setLoading(true);

        const rideDtoResult = await RidesApi.removeEmptyStationFromRide(
          rideId,
          { emptyStationId, secondaryId, skipMlPathByWaypoints },
        );

        if (!rideDtoResult) {
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        if (!rideDtoResult?.success) {
          return rideDtoResult as SimpleResult<void>;
        }

        if (rideDtoResult.data) {
          replaceRide(rideDtoResult.data);
          clearStaleRideEditDtoCacheForRide(rideId);
        }

        Log.successAndToast(t("ride.RIDE_UPDATED_SUCCESSFULLY"));
        return new SimpleResult({ success: true });
      } catch (err) {
        const normalizedErrors = normalizeErrors(err as IApiError, t);
        setErrors(normalizedErrors);
        return new SimpleResult({ error: normalizedErrors });
      } finally {
        setLoading(false);
      }
    },
  );

  const updateRideStationAttributes = useEvent(
    async (
      payload: Partial<IUpdateRideStationDto>,
      rideId: number,
      mode = RideStationPanelMode.GRID_PANEL,
    ): Promise<SimpleResult<void>> => {
      try {
        setErrors(undefined);

        if (mode === RideStationPanelMode.GRID_PANEL) {
          setLoading(true);
          const dtoResult = await RidesApi.updateRideStationAttributes(
            rideId,
            payload,
          );

          if (!dtoResult) {
            setErrors([t(errors.common.emptyApiResponse)]);
            return simpleResultErrorEmptyResponse(t);
          }

          if (!dtoResult.success) {
            setErrors([simpleResultErrorInfo(dtoResult, t)]);
            return castErrorResult(dtoResult);
          }

          if (!dtoResult.data) {
            setErrors([t(errors.common.invalidAPIResponse)]);
            return simpleResultInvalidApiResponse(t);
          }

          const dto = dtoResult.data;
          Log.debug("Replace ride in list", dto);
          replaceRide(dto);
          clearStaleRideEditDtoCacheForRide(rideId);
        } else {
          setRideDto(dto => {
            if (!dto) {
              Log.warnAndToast(
                t(errors.ride.invalidEditDTOWhileUpdatingStationAttributes),
              );
              return;
            }

            const metadata = dto.metadata as IRideMetadata;
            const stations = metadata.stationsInfo || [];
            const stationRow = stations.find(s => s.id === payload.stationId);

            if (!stationRow) {
              Log.warnAndToast(t(errors.ride.didntFoundStationToUpdate));
              return;
            }

            const stationToUpdate = { ...stationRow };

            if (payload.isLocked !== undefined) {
              stationToUpdate.isLocked = payload.isLocked;
            }

            if (payload.comment !== undefined) {
              stationToUpdate.comment = payload.comment;
            }

            const updatedStations: IStationInfo[] = stations.map(x =>
              x.id === payload.stationId ? stationToUpdate : x,
            );
            const updatedMetadata: IRideMetadata = {
              ...metadata,
              stationsInfo: updatedStations,
            };
            const dtoUpdated = { ...dto, metadata: updatedMetadata };
            Log.infoAndToast(t("ride.STATION_ATTRIBUTES_UPDATED"), dtoUpdated);
            return dtoUpdated;
          });
        }

        return new SimpleResult({ success: true });
      } catch (error) {
        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setLoading(false);
      }
    },
  );

  return {
    removePassengersFromRide,
    removeEmptyStationFromRide,
    updateRideStationAttributes,
  };
};
